// "Commercial model" — HOW DriveCars earns on this partner (migration 0241).
//
// The third axis of a partner, after WHAT it sells (products) and WHO it may
// sell (crews): the money. Three models, each with its own rates:
//
//   Booking fee                  the customer pays the crew's base + our 20%
//                                fee, and the partner takes a share OF THAT
//                                FEE. Every partner before 0241 was here.
//   Supplier commission          the customer pays the crew's base and nothing
//                                else; the crew, paid in cash at pickup, owes
//                                DriveCars a percentage of it.
//   Markup + supplier commission both: we mark the base up by our own markup
//                                AND take the crew's commission.
//
// THE RATES A MODEL DOES NOT USE ARE NOT MERELY HIDDEN. The editor keeps the
// whole draft, so a switch from Model 3 to Model 1 would otherwise save the
// markup share it no longer asks for; `withOffModelRatesCleared` (the state
// module) nulls them in the Save payload, and the API nulls the same columns
// server-side whenever the model moves.
//
// UNLIKE the cities and crews fieldsets beside it, this one does NOT save
// itself. Those two are replace-sets against a live fleet, where riding along
// on an unrelated Save could quietly take a crew away. These are six plain
// columns on the partner row, and they belong to the same draft the rest of
// the editor holds — so they go through the ordinary Save, like the commission
// and billing-cycle fields directly above them.
//
// PERCENTAGES ARE TYPED AS WHOLE NUMBERS AND STORED AS FRACTIONS. The
// conversion is in partner-commercial-model-state.ts, with the reasoning; what
// matters here is that the legacy `Commission %` box a few lines up takes a
// raw fraction (0.05) while these take 5, and the note under the radio is what
// stops the two being read as the same kind of field.
//
// WHAT THE NOTE SAYS, AND WHY IT IS NOT DECORATION. On Model 1 a partner is
// paid by one of two rules, and the form has to say which: a set fee share
// pays `fee x share`, and an unset one leaves the pre-0241
// `commission_flat + commission_pct x PRICE` rule in force. They read alike
// and pay very differently — 10% of a 120 price is 12, which is 60% of a 20
// fee. On Models 2 and 3 the note says the other thing ops cannot see from
// here: those partners are served no crew that has agreed no rate, and no
// supplier gap-fill at all.

import {
  COMMERCIAL_MODELS,
  earnsFromCrewTakings,
  fieldsForModel,
  fractionToPercentInput,
  percentInputToFraction,
  shareRuleFor,
  type CommercialModel,
  type CommercialRateField,
} from './partner-commercial-model-state';

/** The rates this fieldset edits, as they sit on the editor draft. */
export interface CommercialRates {
  commercial_model?: CommercialModel;
  fee_share_pct?: number | null;
  markup_pct?: number | null;
  markup_share_pct?: number | null;
  supplier_commission_share_pct?: number | null;
}

export interface PartnerCommercialModelFieldsetProps {
  draft: CommercialRates;
  /**
   * Applies one column to the draft. The parent owns the draft so Save sends
   * the whole thing, exactly as it does for every other field.
   *
   * Deliberately NOT generic over `keyof CommercialRates`. The editor's own
   * `set` is generic over `keyof PartnerWriteInput` — a WIDER key set — and a
   * generic signature narrowed to this fieldset's five keys is not assignable
   * from it (TypeScript compares the two generics' parameters, and
   * `CommercialRates[K]` is not `PartnerWriteInput[K]` for an arbitrary K).
   * A per-field union keeps the call site's type checked at each call —
   * `onChange('markup_pct', number | null)` still refuses a string — without
   * demanding a wider setter be assignable to a narrower one.
   */
  onChange: {
    (key: 'commercial_model', value: CommercialModel): void;
    (key: CommercialRateField, value: number | null): void;
  };
  /** DriveCars' own storefront/app rows. `resolveCommercialTerms` neutralises
   *  them to the plain booking fee whatever their columns hold, and the API
   *  refuses any other model with a 409 — so the radios are disabled here
   *  rather than letting ops meet that refusal. */
  isDriveCarsPartner: boolean;
}

const MODEL_LABEL: Record<CommercialModel, string> = {
  booking_fee: 'Booking fee — the customer pays the crew’s base plus our fee',
  supplier_commission:
    'Supplier commission — the customer pays the crew’s base; the crew owes us a cut of it',
  markup_supplier_commission:
    'Markup + supplier commission — we mark the base up AND take the crew’s cut',
};

/** The label on each rate box. Spelled as a sentence rather than as the column
 *  name: `markup_share_pct` and `supplier_commission_share_pct` are both "a
 *  share" and reading them apart at a glance is the whole difficulty. */
const RATE_LABEL: Record<CommercialRateField, string> = {
  fee_share_pct: 'Partner’s share of the fee (%)',
  markup_pct: 'DriveCars’ markup over the crew’s base (%)',
  markup_share_pct: 'Partner’s share of the markup (%)',
  supplier_commission_share_pct: 'Partner’s share of the crew’s commission (%)',
};

export function PartnerCommercialModelFieldset({
  draft,
  onChange,
  isDriveCarsPartner,
}: PartnerCommercialModelFieldsetProps) {
  // `?? 'booking_fee'` guards a row read back from an API build that predates
  // 0241, the same way booking_mode and auth_provider are guarded above: a
  // missing model must render as the default, not as three unchecked radios
  // ops can save.
  const model: CommercialModel = draft.commercial_model ?? 'booking_fee';
  const fields = fieldsForModel(model);
  const shareRule = shareRuleFor(draft.fee_share_pct);

  return (
    <fieldset>
      <legend className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
        Commercial model
      </legend>
      <p className="mt-1 text-[11.5px] text-[color:var(--bo-mute)]">
        How DriveCars earns on this partner. Applies to new bookings only — a sale already
        made keeps the model it was made under.
      </p>

      <div className="mt-1.5 flex flex-col gap-1.5">
        {COMMERCIAL_MODELS.map((m) => {
          const locked = isDriveCarsPartner && m !== 'booking_fee';
          return (
            <label
              key={m}
              className={`flex items-start gap-1.5 text-[13px] ${
                locked ? 'text-[color:var(--bo-mute)]' : ''
              }`}
            >
              <input
                type="radio"
                name="commercial_model"
                value={m}
                checked={model === m}
                disabled={locked}
                onChange={() => onChange('commercial_model', m)}
                className="mt-0.5"
              />
              <span>{MODEL_LABEL[m]}</span>
            </label>
          );
        })}
      </div>

      {isDriveCarsPartner ? (
        <p className="mt-1.5 text-[12px] text-[color:var(--bo-mute)]">
          DriveCars’ own partners are always priced on the booking fee — there is no partner
          in that sale to share with.
        </p>
      ) : null}

      {/* The rates the CHOSEN model uses, and no others. */}
      <div className="mt-2.5 grid grid-cols-2 gap-3">
        {fields.map((field) => (
          <label key={field} className="block">
            <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
              {RATE_LABEL[field]}
            </span>
            <input
              type="number"
              step="0.1"
              min="0"
              // Whole-number percent on screen, fraction in the column. The
              // value is derived from the draft on every render rather than
              // held in its own state, so switching models and coming back
              // shows what is actually going to be saved.
              value={fractionToPercentInput(draft[field])}
              aria-label={RATE_LABEL[field]}
              onChange={(e) => onChange(field, percentInputToFraction(e.target.value))}
              placeholder={field === 'fee_share_pct' ? 'e.g. 50' : 'optional'}
              className="mt-1 h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13.5px] outline-none focus:border-[color:var(--bo-accent)]"
            />
          </label>
        ))}
      </div>

      {/* WHICH RULE PAYS THIS PARTNER — see the header. */}
      {model === 'booking_fee' ? (
        <p className="mt-1.5 text-[12px] text-[color:var(--bo-mute)]">
          {shareRule === 'fee_share'
            ? 'In force: the partner’s share of the fee above. The Commission % / flat fields are ignored while this is set.'
            : 'In force: the legacy commission (Commission % × the customer’s price, plus the flat amount) — not a share of the fee. Set a fee share above to switch.'}
        </p>
      ) : null}

      {earnsFromCrewTakings(model) ? (
        <p className="mt-1.5 text-[12px] text-[color:var(--bo-mute)]">
          On this model we earn from what the crew collects, so this partner is served only
          crews that have agreed a rate — a crew with no agreed rate is hidden from its
          search, even if it is ticked under Included crews. Gap-fill supply is not offered
          to it at all.
        </p>
      ) : null}
    </fieldset>
  );
}
